import React, { useState } from 'react';
import { ScrollView, View, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import styled from 'styled-components/native';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { Text, Card, AppBar } from '@ui';
import { useNotifications } from '@api/hooks';
import { useTheme } from '@theme/ThemeProvider';
import { Notification } from '@mocks/notifications';
import { RootStackParamList } from '@navigation/types';

const Tab = createMaterialTopTabNavigator();

const Container = styled(SafeAreaView)`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const TabContent = styled(ScrollView)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const NotificationCard = styled(TouchableOpacity)<{ read: boolean }>`
  flex-direction: row;
  background-color: ${({ theme, read }) => (read ? theme.colors.surface : theme.colors.surfaceAlt)};
  border-radius: ${({ theme }) => theme.radius.md}px;
  padding: ${({ theme }) => theme.spacing.base}px;
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
  border-width: 1px;
  border-color: ${({ theme }) => theme.colors.border};
  min-height: ${({ theme }) => theme.minTouchTarget}px;
`;

const IconWrapper = styled.View<{ category: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background-color: ${({ theme, category }) => {
    switch (category) {
      case 'tasks':
        return theme.colors.warningLight;
      case 'benefits':
        return theme.colors.successLight;
      case 'advisor':
        return theme.colors.infoLight;
      default:
        return theme.colors.neutral100;
    }
  }};
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.base}px;
`;

const NotificationContent = styled.View`
  flex: 1;
`;

const NotificationHeader = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.xs}px;
`;

const UnreadDot = styled.View`
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.brandPrimary};
  margin-left: ${({ theme }) => theme.spacing.sm}px;
`;

const getIconForCategory = (category: string): string => {
  switch (category) {
    case 'tasks':
      return 'task-alt';
    case 'benefits':
      return 'card-giftcard';
    case 'advisor':
      return 'person';
    default:
      return 'notifications';
  }
};

const getIconColorForCategory = (category: string, theme: any): string => {
  switch (category) {
    case 'tasks':
      return theme.colors.warning;
    case 'benefits':
      return theme.colors.success;
    case 'advisor':
      return theme.colors.info;
    default:
      return theme.colors.textSecondary;
  }
};

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const NotificationItem: React.FC<{ notification: Notification }> = ({ notification }) => {
  const navigation = useNavigation<NavigationProp>();
  const theme = useTheme();

  return (
    <NotificationCard
      read={notification.read}
      activeOpacity={0.7}
      onPress={() => navigation.navigate('InboxDetail', { notification })}
    >
      <IconWrapper category={notification.category}>
        <Icon
          name={getIconForCategory(notification.category)}
          size={24}
          color={getIconColorForCategory(notification.category, theme)}
        />
      </IconWrapper>
      <NotificationContent>
        <NotificationHeader>
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
            <Text variant="titleMedium" color="textPrimary">
              {notification.title}
            </Text>
            {!notification.read && <UnreadDot />}
          </View>
        </NotificationHeader>
        <Text variant="body" color="textSecondary" numberOfLines={2} style={{ marginTop: 4 }}>
          {notification.message}
        </Text>
        <Text variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
          {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
        </Text>
        {notification.actionLabel && (
          <Text variant="body" color="brandPrimary" style={{ marginTop: 12, fontWeight: '600' }}>
            {notification.actionLabel} →
          </Text>
        )}
      </NotificationContent>
    </NotificationCard>
  );
};

const TabScreen: React.FC<{ category: 'all' | 'tasks' | 'benefits' | 'advisor' }> = ({
  category,
}) => {
  const { data: notifications } = useNotifications();

  const filtered =
    category === 'all' ? notifications : notifications?.filter(n => n.category === category);

  return (
    <TabContent showsVerticalScrollIndicator={false}>
      {filtered?.map(notification => (
        <NotificationItem key={notification.id} notification={notification} />
      ))}
      {filtered?.length === 0 && (
        <Card padding="lg" style={{ marginTop: 24 }}>
          <Text variant="body" color="textSecondary" align="center">
            No notifications
          </Text>
        </Card>
      )}
      <View style={{ height: 24 }} />
    </TabContent>
  );
};

const InboxScreen: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Container edges={['top']}>
      <AppBar title={t('inbox.title')} />
      <Tab.Navigator
        screenOptions={{
          tabBarActiveTintColor: theme.colors.brandPrimary,
          tabBarInactiveTintColor: theme.colors.textSecondary,
          tabBarPressColor: `${theme.colors.brandPrimary}15`,
          tabBarIndicatorStyle: {
            backgroundColor: theme.colors.brandPrimary,
            height: 4,
          },
          tabBarLabelStyle: {
            fontSize: theme.typography.bodyMedium.fontSize,
            fontWeight: theme.typography.titleMedium.fontWeight,
            textTransform: 'none',
            letterSpacing: 0,
          },
          tabBarStyle: {
            backgroundColor: theme.colors.surface,
            elevation: 0,
            shadowOpacity: 0,
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.border,
            paddingHorizontal: theme.spacing.xs,
          },
          tabBarItemStyle: {
            paddingVertical: theme.spacing.sm,
          },
          tabBarScrollEnabled: false,
        }}
      >
        <Tab.Screen name="All" options={{ title: t('inbox.all') }}>
          {() => <TabScreen category="all" />}
        </Tab.Screen>
        <Tab.Screen name="Tasks" options={{ title: t('inbox.tasks') }}>
          {() => <TabScreen category="tasks" />}
        </Tab.Screen>
        <Tab.Screen name="Benefits" options={{ title: t('inbox.benefits') }}>
          {() => <TabScreen category="benefits" />}
        </Tab.Screen>
        <Tab.Screen name="Advisor" options={{ title: t('inbox.advisor') }}>
          {() => <TabScreen category="advisor" />}
        </Tab.Screen>
      </Tab.Navigator>
    </Container>
  );
};

export default InboxScreen;
